import { MAX_ADHOC_FIELDS_PER_PERSON } from '@sentra/contracts';
import { Plus, Trash2 } from 'lucide-react';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import type { AdhocDraft, AdhocProblem } from '@/features/persons/form/values';

/**
 * The operator's own fields for one record: a name, a value, and at most ten of them.
 *
 * **The eleventh row is refused HERE as well as by the API**, and the "add" control disappears at
 * the tenth. Both halves matter: the server's `adhocFieldsSchema` is what makes the cap true, and a
 * form that let somebody fill in an eleventh row and then refused the whole submission would have
 * taken their work and explained afterwards.
 *
 * **The name offers what this terminal has used before**, through a `datalist`. The platform's own
 * autocomplete needs no keyboard handling, no ARIA and no filtering code, and it does not block
 * typing something new — which is the point of an ad-hoc field. What is remembered is the LABEL and
 * never the value; `adhoc-names.ts` states why that distinction is the whole design.
 */
export function AdhocFields({
  drafts,
  suggestions,
  problem,
  onChange,
}: Readonly<{
  drafts: readonly AdhocDraft[];
  /** Names this terminal has used before, plus any already on the record. */
  suggestions: readonly string[];
  problem: AdhocProblem | null;
  onChange: (next: readonly AdhocDraft[]) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const listId = useId();
  const full = drafts.length >= MAX_ADHOC_FIELDS_PER_PERSON;

  function replace(index: number, patch: Partial<AdhocDraft>): void {
    onChange(drafts.map((draft, at) => (at === index ? { ...draft, ...patch } : draft)));
  }

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-sm font-medium">{t('person.form.adhocLegend')}</legend>
      <p className="text-xs text-muted-foreground">
        {t('person.form.adhocHint', { limit: MAX_ADHOC_FIELDS_PER_PERSON })}
      </p>

      <datalist id={listId}>
        {suggestions.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>

      {drafts.map((draft, index) => (
        // The index is the key on purpose: these rows have no identity of their own until they are
        // named, and keying on the name would remount the row on every keystroke, losing the caret
        // and the operator's place in it.
        <div key={index} className="flex items-end gap-2" data-adhoc-row={index}>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor={`adhoc-name-${String(index)}`}>{t('person.form.adhocName')}</Label>
            <Input
              id={`adhoc-name-${String(index)}`}
              list={listId}
              autoComplete="off"
              value={draft.name}
              onChange={(event) => {
                replace(index, { name: event.target.value });
              }}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor={`adhoc-value-${String(index)}`}>{t('person.form.adhocValue')}</Label>
            <Input
              id={`adhoc-value-${String(index)}`}
              autoComplete="off"
              value={draft.value}
              onChange={(event) => {
                replace(index, { value: event.target.value });
              }}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('person.form.adhocRemove')}
            onClick={() => {
              onChange(drafts.filter((_row, at) => at !== index));
            }}
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      ))}

      {problem !== null && (
        <p role="alert" className="text-xs text-destructive">
          {problem.kind === 'limit'
            ? t('person.form.adhocLimit', { limit: problem.limit })
            : t('person.form.adhocDuplicateName')}
        </p>
      )}

      {!full && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => {
            onChange([...drafts, { name: '', value: '' }]);
          }}
        >
          <Plus aria-hidden />
          {t('person.form.adhocAdd')}
        </Button>
      )}
      {full && <p className="text-xs text-muted-foreground">{t('person.form.adhocFull')}</p>}
    </fieldset>
  );
}
